// AddDoctorModal.jsx - Admin Add Doctor Form Modal
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import '../../styles/components/admin/AddDoctorModal.css';
import adminService from '../../services/adminService';

const AddDoctorModal = ({ isOpen, onClose, onDoctorAdded }) => {
  const [tenants, setTenants] = useState([]);
  const [isLoadingTenants, setIsLoadingTenants] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    tenantId: '',
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    verificationStatus: 'approved',
    role: 'doctor'
  });

  // Fetch tenants when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchTenants();
      resetForm();
    }
  }, [isOpen]);

  const fetchTenants = async () => {
    try {
      setIsLoadingTenants(true);
      console.log('🔄 Fetching tenants for admin add doctor...');
      
      const response = await adminService.getTenants();
      console.log('✅ Tenants fetched:', response);
      
      let tenantsData = [];
      if (response && response.data) {
        tenantsData = response.data;
      } else if (Array.isArray(response)) {
        tenantsData = response;
      }
      
      if (Array.isArray(tenantsData) && tenantsData.length > 0) {
        setTenants(tenantsData);
        // Auto-select first tenant if only one exists
        if (tenantsData.length === 1) {
          setFormData(prev => ({ ...prev, tenantId: tenantsData[0]._id }));
        }
      } else {
        console.log('⚠️ No tenants data found');
        toast.warning('No clinics available. Please add a clinic first.');
      }
    } catch (error) {
      console.error('❌ Error fetching tenants:', error);
      toast.error('Failed to load clinics. Please try again.');
    } finally {
      setIsLoadingTenants(false);
    }
  };

  const resetForm = () => {
    setFormData({
      tenantId: '',
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      verificationStatus: 'approved',
      role: 'doctor'
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    const errors = [];
    
    if (!formData.tenantId) errors.push('Please select a clinic');
    if (!formData.firstName.trim()) errors.push('First name is required');
    if (!formData.lastName.trim()) errors.push('Last name is required');
    if (!formData.email.trim()) errors.push('Email is required');
    if (!formData.email.includes('@')) errors.push('Valid email is required');
    if (!formData.password.trim()) errors.push('Password is required');
    if (formData.password.length < 6) errors.push('Password must be at least 6 characters');
    if (!formData.confirmPassword.trim()) errors.push('Please confirm password');
    if (formData.password !== formData.confirmPassword) errors.push('Passwords do not match');
    
    if (errors.length > 0) {
      errors.forEach(error => toast.error(error));
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      console.log('🔄 Admin adding doctor with data:', formData);
      
      // Prepare the data for submission (exclude confirmPassword)
      const { confirmPassword, ...doctorData } = formData;
      
      const submissionData = {
        ...doctorData,
        verificationStatus: 'approved',
        verificationDate: new Date().toISOString(),
        isAdminAdded: true,
        verificationNotes: 'Added by admin - automatically approved. Profile to be completed by doctor.'
      };
      
      // Call the API to add doctor
      const response = await adminService.addDoctor(submissionData);
      
      console.log('✅ Doctor added successfully:', response);
      toast.success('Doctor account created successfully! They can now complete their profile.');
      
      // Call the callback to refresh the parent component
      if (onDoctorAdded) {
        onDoctorAdded();
      }
      
      // Close the modal
      onClose();
      
    } catch (error) {
      console.error('❌ Error adding doctor:', error);
      
      let errorMessage = 'Failed to add doctor. Please try again.';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.errors) {
        const errors = error.response.data.errors;
        if (Array.isArray(errors)) {
          errorMessage = errors.join(', ');
        } else if (typeof errors === 'object') {
          errorMessage = Object.values(errors).join(', ');
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="admin-add-doctor-modal-overlay" onClick={onClose}>
      <div className="admin-add-doctor-modal" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="admin-add-doctor-modal-header">
          <h2 className="admin-add-doctor-modal-title">Add New Doctor</h2>
          <button className="admin-add-doctor-modal-close" onClick={onClose} disabled={isSubmitting}>
            <span className="admin-add-doctor-close-icon">×</span>
          </button>
        </div>

        {/* Modal Content */}
        <div className="admin-add-doctor-modal-content">
          <form onSubmit={handleSubmit} className="admin-add-doctor-form">
            <div className="admin-add-doctor-step">
              <div className="admin-add-doctor-form-group">
                <label htmlFor="tenantId" className="admin-add-doctor-label">Clinic Location</label>
                <select
                  id="tenantId"
                  name="tenantId"
                  className="admin-add-doctor-select"
                  value={formData.tenantId}
                  onChange={handleChange}
                  disabled={isLoadingTenants}
                  required
                >
                  <option value="">Select a clinic</option>
                  {tenants.map(tenant => (
                    <option key={tenant._id} value={tenant._id}>
                      {tenant.name}
                    </option>
                  ))}
                </select>
                {isLoadingTenants && <div className="admin-add-doctor-loading-message">Loading clinics...</div>}
              </div>
              
              <div className="admin-add-doctor-form-row">
                <div className="admin-add-doctor-form-group">
                  <label htmlFor="firstName" className="admin-add-doctor-label">First Name</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    className="admin-add-doctor-input"
                    value={formData.firstName}
                    placeholder='First Name'
                    onChange={handleChange}
                    required
                  />
                </div>
                
                <div className="admin-add-doctor-form-group">
                  <label htmlFor="lastName" className="admin-add-doctor-label">Last Name</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    className="admin-add-doctor-input"
                    placeholder='Last Name'
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
              
              <div className="admin-add-doctor-form-group">
                <label htmlFor="email" className="admin-add-doctor-label">Email Address</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  className="admin-add-doctor-input"
                  placeholder='Email'
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>
              
              <div className="admin-add-doctor-form-row">
                <div className="admin-add-doctor-form-group">
                  <label htmlFor="password" className="admin-add-doctor-label">Password</label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    className="admin-add-doctor-input"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Minimum 6 characters"
                    required
                  />
                </div>
                
                <div className="admin-add-doctor-form-group">
                  <label htmlFor="confirmPassword" className="admin-add-doctor-label">Confirm Password</label>
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    className="admin-add-doctor-input"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="Re-enter password"
                    required
                  />
                </div>
              </div>
            </div>
            
            {/* Form Actions */}
            <div className="admin-add-doctor-form-actions">
              <button 
                type="submit" 
                className="admin-add-doctor-btn-primary admin-add-doctor-btn-submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Creating Account...' : 'Create Doctor Account'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddDoctorModal;